import React, { FC, useCallback, useEffect, useState } from 'react';
import { Token, useApiIntegrations } from 'src/common/api/use-integrations.hook';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { STEP_EDITOR_CONTENT_OPTION_URL } from 'src/editor/constants';
import GooglePicker from 'src/integrations/GooglePicker';
import { GoogleDriveIntegrationIcon } from 'src/monet/icons';
import { useStepEditorContext } from 'src/store/step-editor';
import LoadingSpinner from 'src/common/components/loading-spinner';
import { useEmbedUrlChange } from '../../use-embed-url-change.hook';
import { StepEditorContentConfigureStageTypeEnum } from '../configure/stage-type.enum';

type StepEditorContentLibraryIntegrationsProps = {
  forceUpdateCounter: number;
};

export const StepEditorContentLibraryIntegrations: FC<StepEditorContentLibraryIntegrationsProps> = ({
  forceUpdateCounter,
}) => {
  const [tokens, setTokens] = useState<Token[]>([]);
  const [fetching, setFetching] = useState<boolean>(false);
  const { checkEmbedUrl } = useEmbedUrlChange();

  const { setUrl, setName, setContentOption, setCurrentConfigurationStage, setContentName } = useStepEditorContext();

  const { fetchAccessTokens } = useApiIntegrations();

  const onFileSelected = useCallback(
    async (url: string, name: string) => {
      setCurrentConfigurationStage(StepEditorContentConfigureStageTypeEnum.CONFIGURE);
      setContentOption(STEP_EDITOR_CONTENT_OPTION_URL);
      setUrl(url);
      setName(name);
      await checkEmbedUrl(url);
      setContentName(name);
    },
    [setUrl, setName, setContentOption]
  );

  const fetchAndSetAccessTokens = useCallback(
    async (silent: boolean) => {
      try {
        !silent && setFetching(true);
        setTokens(await fetchAccessTokens('google-drive'));
      } catch {
        // Nothing to do
      } finally {
        !silent && setFetching(false);
      }
    },
    [fetchAccessTokens]
  );

  useEffect(() => {
    fetchAndSetAccessTokens(false);
  }, [fetchAndSetAccessTokens, forceUpdateCounter]);

  const { setErrorNotification } = useNotificationMessage();

  const onChange = useCallback(
    async (data, google, updateFilePermissionToPublic) => {
      if (data.action !== google.picker.Action.PICKED) return;
      let doc = data[google.picker.Response.DOCUMENTS][0];
      const docId = doc.id;

      const submitFile = () => {
        let embedUrl = doc[google.picker.Document.EMBEDDABLE_URL];
        let name = doc[google.picker.Document.NAME];
        onFileSelected(embedUrl, name);
      };

      try {
        await updateFilePermissionToPublic(docId);
      } catch {
        // Nothing to do
      } finally {
        submitFile();
      }
    },
    [onFileSelected]
  );

  if (fetching) {
    return (
      <div>
        <LoadingSpinner />
      </div>
    );
  }
  if (tokens.length === 0) {
    return <div className='text-bedrock-p'>No integrations found</div>;
  }
  return (
    <div className='flex flex-col items-start space-y-1'>
      {tokens.map((token) => (
        <GooglePicker
          accessToken={token.access_token}
          fetchTokens={() => fetchAndSetAccessTokens(true)}
          setError={setErrorNotification}
          mimeTypes={null}
          navHidden={true}
          tokenID={token.id}
          onChange={onChange}
        >
          <button type='button' className='flex items-center btn-bedrock-secondary px-4 py-[9px] space-x-1'>
            <GoogleDriveIntegrationIcon className='w-5 h-5' />
            <div className='text-bedrock-p-strong'>{token.email}</div>
          </button>
        </GooglePicker>
      ))}
    </div>
  );
};
